import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Plus, RefreshCw, Tag } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PERIODICITY_LABELS, billingError, canWrite, priceText } from "../lib/billing";
import { RevenueTabs } from "../components/billing/BillingBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

function NewOfferDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setName(""); setDescription(""); setError(""); } }, [open]);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/billing/offers", { name: name.trim(), description: description.trim() });
      navigate(`/revenus/offres/${data.id}`);
    } catch (e) {
      setError(billingError(e, "Création impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-offer-new-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle offre</DialogTitle>
          <DialogDescription>Vous fixerez ses prix dans un brouillon, puis le publierez pour le proposer aux pharmacies.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="billing-offer-name">Nom</Label>
            <Input id="billing-offer-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Essentiel, Pro, Réseau…"
              className="h-10" data-testid="billing-offer-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="billing-offer-description">Description (facultatif)</Label>
            <Textarea id="billing-offer-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000} />
          </div>
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || name.trim().length < 2} data-testid="billing-offer-create">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer l'offre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function prices(offer) {
  const content = offer.published?.content;
  if (!content) return <span className="text-slate-400">Pas encore publiée</span>;
  if (content.on_quote) return "Sur devis";
  return content.periods.map((p) => (
    <span key={p.periodicity} className="block">{priceText(p.price_minor, content.currency, p.periodicity)}</span>
  ));
}

export default function BillingOffers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const canManage = canWrite(user, "billing:manage_offers");

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get("/billing/offers");
      setItems(data.items);
      setPhase("ready");
    } catch (e) {
      setError(billingError(e, "Impossible de charger les offres."));
      setPhase("error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="billing-offers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Offres et abonnements</h1>
          <p className="text-sm text-slate-500">Les offres que Jamora vend aux pharmacies : un nouveau prix se publie en nouvelle version, sans toucher aux abonnements en cours.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)} data-testid="billing-offer-new"><Plus size={16} className="mr-1.5" />Nouvelle offre</Button>}
      </div>
      <RevenueTabs />

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="billing-offers-empty">
          <Tag className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucune offre pour le moment</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Saisissez la grille tarifaire de Jamora (Essentiel, Pro, Réseau…) pour pouvoir abonner les pharmacies.</p>
          {canManage && <Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1.5" />Nouvelle offre</Button>}
        </Card>
      )}
      {phase === "ready" && items.length > 0 && (
        <Card className="divide-y divide-slate-100" data-testid="billing-offers-list">
          {items.map((o) => (
            <Link key={o.id} to={`/revenus/offres/${o.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`billing-offer-${o.name}`}>
              <div className="min-w-0 flex-1 grid gap-1 sm:grid-cols-[minmax(0,1fr)_14rem_10rem] sm:items-center">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{o.name}{!o.active && <span className="ml-2 text-xs text-slate-500">Archivée</span>}</div>
                  <div className="text-xs text-slate-500">
                    {o.published ? `Version ${o.published.number}` : "Brouillon"}{o.draft && o.published ? " · un brouillon en cours" : ""}
                    {o.published?.content?.periods?.length ? ` · ${o.published.content.periods.map((p) => PERIODICITY_LABELS[p.periodicity].toLowerCase()).join(", ")}` : ""}
                  </div>
                </div>
                <div className="text-sm text-slate-700 tabular-nums">{prices(o)}</div>
                <div className="text-sm text-slate-500">{o.running_subscriptions} abonnement{o.running_subscriptions > 1 ? "s" : ""} en cours</div>
              </div>
              <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
            </Link>
          ))}
        </Card>
      )}
      <NewOfferDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
